import isFuture from 'date-fns/isFuture';
import isValidDomain from 'is-valid-domain';
import { RegisterOptions } from 'react-hook-form';

import { forms } from './dictionary/forms';

const tlds = require('tld-list');

export const required: RegisterOptions = {
  validate: (value: any) => (typeof value === 'string' ? Boolean(value.trim()) : Boolean(value)) || forms.fieldRequired,
};

export const dateInFuture: RegisterOptions = {
  validate: (value: Date) => {
    if (!value) {
      return true;
    }
    if (!isFuture(value)) {
      return forms.dateInTheFuture;
    }
    return true;
  },
};

export const requiredPromoCode: RegisterOptions = {
  required: {
    value: true,
    message: forms.invalidPromoCode,
  },
};

export const email: RegisterOptions = {
  pattern: {
    value: /^[a-z\d]+[+\w\d.-]*@(?:[a-z\d]+[a-z\d-]+\.){1,5}[a-z]{2,6}$/i,
    message: forms.invalidEmail,
  },
  validate: (value: string) => {
    if (!value) {
      return forms.fieldRequired;
    }
    const emailValue = value.replaceAll(' ', '').split('@');

    if (!emailValue.length) {
      return forms.fieldRequired;
    }

    const emailValueLast = emailValue[1].split('.');
    const emailValueTLD = emailValueLast[emailValueLast.length - 1];
    const emailRegex1 = new RegExp('^(.+)@(\\S+)$');

    if (!emailRegex1.test(value.replaceAll(' ', ''))) {
      return forms.invalidEmail;
    }
    if (!isValidDomain(emailValue[1])) {
      return forms.invalidEmail;
    }
    if (!tlds.find((tld: any) => tld === emailValueTLD)) {
      return forms.invalidEmail;
    }

    return true;
  },
};

export const phoneNumber: RegisterOptions = {
  pattern: {
    value:
      /^\+?(\(?(\+?[0-9]{1,3})\)?)?[-\s.\/]?[0-9]{3}[-\s.\/]?[0-9]{3,6}[-\s.\/]?([0-9]{3,6})?[-\s.\/]?(x[0-9]{1,6})?$/im,
    message: forms.invalidPhone,
  },
};

export const minNumber = (number: number, message = forms.invalidNoOfWords): RegisterOptions => ({
  min: {
    value: number,
    message: `${message} ${number - 1}.`,
  },
});

export const noEmptyArray = (maxValue = Number.MAX_SAFE_INTEGER): RegisterOptions => ({
  validate: (value: any) => {
    if (!(Array.isArray(value) && value.length > 0)) {
      return forms.invalidSelect;
    }
    if (value.length > maxValue) {
      return `${forms.toManyElements} ${maxValue}`;
    }
    return true;
  },
});

export const creditCardHolderName: RegisterOptions = {
  ...required,
  pattern: {
    value: /^[a-z,.'-~\s]{2,24}\s[a-z,.'-~\s]{2,24}$/i,
    message: forms.invalidCreditCardHolderName,
  },
};

export const creditCardNumber: RegisterOptions = {
  validate: (value: string) => {
    const cardNumberRegex =
      /^(?!34)(?!37)(?:4[0-9]{12}(?:[0-9]{3})?|[25][1-7][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\d{3})\d{11})$/;
    if (!value) {
      return forms.fieldRequired;
    }
    return cardNumberRegex.test(value.replaceAll(' ', '')) || forms.invalidCreditCardNumber;
  },
};

export const expirationMonth: RegisterOptions = {
  validate: (value: string) => {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue) || value.split('-').join().length !== 2) {
      return forms.invalidExpirationMonth;
    }
    return (numberValue > 0 && numberValue <= 12) || forms.invalidMonth;
  },
};

export const expirationYear: RegisterOptions = {
  validate: (value: string) =>
    (!Number.isNaN(Number(value)) && Number(value) >= new Date().getFullYear() && value.length === 4) ||
    forms.invalidExpirationYear,
};

export const isInteger: RegisterOptions = {
  pattern: {
    value: /^\d+$/,
    message: forms.integerValueRequired,
  },
};

export const validateNumberOfWordsAndFiles = (getValues: (field: string) => string[]): RegisterOptions => ({
  ...minNumber(1, forms.minLength),
  ...isInteger,
  validate: (noOfWords: string) => {
    const MAX_NO_OF_WORDS = 2000000000;

    const files = getValues('files');
    if (Number(noOfWords) > MAX_NO_OF_WORDS) {
      return forms.noOfWordsTooBig;
    }

    return Number(noOfWords) > 0 || Boolean(files.length) || forms.uploadFilesOrNoOfWordsRequired;
  },
});

export const isSourceLanguageIncludedDestinationLanguages = (
  getValues: (data: string) => string,
  maxLength = Number.MAX_SAFE_INTEGER
): RegisterOptions => ({
  validate: (destinationLanguages: string[]) => {
    const sourceLanguage = getValues('sourceLanguage');

    if (destinationLanguages.length > maxLength) {
      return `${forms.toManyElements} ${maxLength}`;
    }

    if (destinationLanguages.length) {
      return !destinationLanguages.includes(sourceLanguage) || forms.destinationLanguagesIncludeSourceLanguage;
    }

    return forms.invalidSelect;
  },
});

export const defaultTrue: RegisterOptions = {
  validate: () => true,
};

export const valueIsNumber: RegisterOptions = {
  validate: (value: string) => {
    const numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      return forms.invalidNumber;
    }
    return numberValue > 0 || forms.invalidNumber;
  },
};
